import { useEffect, useRef, useState } from "react";
import type Page from "../models/Page";
import Point from "../models/Point";
import type Room from "../models/Room";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { dataUrlToImage, eventPointToDrawingArea } from "../modelViews/GraficElementUtils";
import Logger from "../Logger";
import BackgroundImagePosition from "../models/BackgroundImagePosition";
import { useRedraw } from "../models/useRerender";
import BoundaryRect from "../models/BoundaryRect";

import { RenderTarget, useRenderTarget } from "./renderPrimitives/renderTarget";
import { RImg } from "./renderPrimitives/RImg";

const logger = new Logger();

export default function BackgroundImage(props: { page: Page }) {
  const { page } = props;

  const [url, setUrl] = useState<string | undefined>(page._clippedImageUrl);

  const initialPos = useRef(new BackgroundImagePosition());
  const initialURL = useRef("");
  const backgroundImageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const pLastMousePositionInDrawingSpace = useRef(new Point(0, 0));
  const pMouseDownInDrawingSpace = useRef(new Point(0, 0));
  const redraw = useRedraw();
  const renderTarget = useRenderTarget();

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(false);

  const forPdf = renderTarget === RenderTarget.pdf;

  async function init() {
    //
    // for Pdf BackgroundImages are preloaded
    //
    if (!forPdf && mountedRef.current) {
      const nextUrl = await page.createClippedImage();
      if (!mountedRef.current) return;
      await page.adjustBackgroundImageForPdf();
      if (mountedRef.current) setUrl(nextUrl);
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    ///createClippedImage();
    void init();

    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  useOnChangeWatcher(page.parentRoom, (changedProperty) => {
    switch (changedProperty) {
      case "selected":
        return false;

      case "boundingRectInFloorSpace": // no itx
      default:
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
          timerRef.current = undefined;
          const nextUrl = await page.createClippedImage();
          if (!mountedRef.current) return;
          setUrl(nextUrl);
          redraw();
        }, 50);
    }
    return false;
  });

  useOnChangeWatcher((page.parent as Room)?.roomtoFloorDrawingTransform, () => {
    const room = page.parentRoom;

    if (backgroundImageRef.current && room && room.roomtoFloorDrawingTransform) {
      const t = room.roomtoFloorDrawingTransform.getCssTransformation1(room);
      backgroundImageRef.current.style.left = t.left + "px";
      backgroundImageRef.current.style.top = t.top + "px";

      backgroundImageRef.current.style.transform = `scale(${t.scale})`;
    }

    return false;
  });

  useOnChangeWatcher(page.adjustActive, () => {
    initialPos.current = page.backgroundImagePosition.copy();
    initialURL.current = page.backgroundImgUrl;
    state.selectionManager.clear();
    return true;
  });

  useOnChangeWatcher(page, (changedProperty) => {
    switch (changedProperty) {
      case "backgroundImagePosition": // no itx
        if (backgroundImageRef.current) {
          if (!page.isBackgroundImageFromFloorPlan) {
            backgroundImageRef.current.style.opacity = ((100 - page.backgroundImagePosition.fade) / 100).toString();
          }
          backgroundImageRef.current.style.transform = page.backgroundImagePosition.calcBackgroundImageTransform();
          return false;
        }
        break;

      case "backgroundImgUrl": // no itx
        init();
        return true;
    }

    return false; // no rerender
  });

  // Panning logic
  const mouseIsDown = useRef(false);

  function onMouseDown(event: any) {
    const p = eventPointToDrawingArea(event);
    if (p) {
      pLastMousePositionInDrawingSpace.current = p;
      pMouseDownInDrawingSpace.current = p;

      mouseIsDown.current = true;
      initialPos.current = page.backgroundImagePosition.copy();

      window.addEventListener("mouseup", onMouseUp);
      event.stopPropagation();
    }
  }

  function onMouseMove(event: any) {
    // logger.log("onMouseMove BackgroundImage", event.buttons, event);

    if (mouseIsDown.current) {
      const p = eventPointToDrawingArea(event);
      if (p) {
        const dx = p.x - pLastMousePositionInDrawingSpace.current.x;
        const dy = p.y - pLastMousePositionInDrawingSpace.current.y;

        page.backgroundImagePosition.posX += dx;
        page.backgroundImagePosition.posY += dy;
        page.raiseOnChange("backgroundImagePosition");

        pLastMousePositionInDrawingSpace.current = p;
      }
      event.stopPropagation();
    }
  }

  function onMouseUp(event: any) {
    if (mouseIsDown.current == true) {
      mouseIsDown.current = false;
      window.removeEventListener("mouseup", onMouseUp);
      event.stopPropagation();

      if (page.isRoomPlan) {
        const room = page.parent as Room;

        if (page.isRoomPlan && page.isBackgroundImageFromFloorPlan && room && room.roomtoFloorDrawingTransform) {
          const boundingRect = room.boundingRectInFloorSpace;

          const pLastMousePositionInFloorSpace = room.roomtoFloorDrawingTransform.transformPoint(
            pLastMousePositionInDrawingSpace.current,
          );
          const pMouseDownInFloorSpace = room.roomtoFloorDrawingTransform.transformPoint(
            pMouseDownInDrawingSpace.current,
          );

          const dx = pLastMousePositionInFloorSpace.x - pMouseDownInFloorSpace.x;
          const dy = pLastMousePositionInFloorSpace.y - pMouseDownInFloorSpace.y;

          let newBoundaryRect = new BoundaryRect(
            new Point(boundingRect.min.x - dx, boundingRect.min.y - dy),

            new Point(boundingRect.max.x - dx, boundingRect.max.y - dy),
          );

          page.freezeFloorPosition();
          room.boundingRectInFloorSpace = newBoundaryRect;
          room.roomtoFloorDrawingTransform.sizeAndPositionFromBoundingRectWithBorder(newBoundaryRect, page);
          page.unfreezeFloorPosition();

          void page.createClippedImage().then((url) => {
            if (!mountedRef.current) return;
            page.backgroundImagePosition = initialPos.current.copy();
            setUrl(url);
            if (backgroundImageRef.current) {
              //
              // set manual, not handled correct (not update) by react
              //
              backgroundImageRef.current.style.transform = page.backgroundImagePosition.calcBackgroundImageTransform();
            }
            //           page.parentRoom?.raiseOnChange();
          });
        }
      }
    }
  }

  if (!url) {
    return null;
  }

  const enableMoveScale = page.adjustActive.value;

  let opacity = ((100 - page.backgroundImagePosition.fade) / 100).toString();
  if (page.isBackgroundImageFromFloorPlan) {
    opacity = "1";
  }

  return (
    <div
      style={{
        position: "absolute",
        left: "0px",
        top: "0px",
        width: "100%",
        height: "100%",
        clipPath: "inset(0)",
      }}
    >
      <div
        id="backgroundImageWrapper"
        ref={backgroundImageRef}
        onMouseDown={enableMoveScale ? onMouseDown : undefined}
        onMouseMove={enableMoveScale ? onMouseMove : undefined}
        onMouseUp={enableMoveScale ? onMouseUp : undefined}
        onTouchStart={enableMoveScale ? onMouseDown : undefined}
        onTouchMove={enableMoveScale ? onMouseMove : undefined}
        onTouchEnd={enableMoveScale ? onMouseUp : undefined}
        // onWheel={onWheel}
        draggable="false" // no itx
        style={{
          cursor: enableMoveScale ? "move" : "default",
          position: "absolute",
          left: "0px",
          top: "0px",
          transformOrigin: "center",
          transform: page.backgroundImagePosition.calcBackgroundImageTransform(),
          opacity: opacity,
          pointerEvents: enableMoveScale ? "all" : undefined, // no itx
        }}
      >
        {url && (
          <RImg
            noOptimize={true}
            style={{
              position: "absolute",
              width: page.backgroundImagePosition.width ? `${page.backgroundImagePosition.width}px` : undefined,
              height: page.backgroundImagePosition.height ? `${page.backgroundImagePosition.height}px` : undefined,
            }}
            id="backgroundImage-web"
            draggable="false" // no itx
            ref={imgRef}
            src={url}
          ></RImg>
        )}
      </div>
    </div>
  );
}
